import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import './FAQPage.css';

const FAQ_SECTIONS = [
  {
    title: 'Our Services',
    items: [
      {
        q: 'What services does Lava & Co offer?',
        a: 'We offer Regular Cleaning for everyday upkeep, Deep Cleaning for a detailed top-to-bottom refresh, Standard Maid Service, Maid Service with Linen Change, and specialty cleanings including post-construction, post-event, and move-in/move-out services. We serve private residences, luxury homes, short-term rentals, and select commercial spaces.',
      },
      {
        q: "What's included in a Regular Cleaning?",
        a: 'Regular cleaning covers everyday upkeep — wiping surfaces, cleaning bathrooms, floor care, tidying the kitchen, and trash removal — keeping your home consistently fresh, clean, and well-maintained.',
      },
      {
        q: "What's included in a Deep Cleaning?",
        a: "Deep cleaning is a detailed, top-to-bottom service for homes needing extra attention. We focus on baseboards, fixtures, and often-overlooked areas to give your home a refined, like-new feel. It's fully customizable to your needs.",
      },
      {
        q: 'What is the Maid Service with Linen Change?',
        a: 'It includes our full maid service plus washing, replacing, and resetting bedding and towels so the home is fully refreshed and guest-ready — like a hotel refresh without checking out. Linen service is offered as part of our cleaning packages and is not a standalone service.',
      },
      {
        q: 'Do you offer add-on services?',
        a: 'Yes! Popular add-ons include interior window cleaning, blinds, ceiling fans, balcony/terrace cleaning, personal laundry, pet hair removal, dishwasher interior cleaning, trash can sanitizing, post-party clean-up, and move-in/move-out deep cleans. You can select them during booking.',
      },
    ],
  },
  {
    title: 'Pricing & Payments',
    items: [
      {
        q: 'How is pricing determined?',
        a: "Pricing is based on your property's size, condition, and specific requirements. Our online booking gives you a transparent, tailored quote instantly based on square footage and the service you choose — no hidden fees.",
      },
      {
        q: 'When do I pay?',
        a: 'Services are paid in advance to secure your booking. Payment is processed securely through our website at the time of booking, and you will receive an email receipt.',
      },
      {
        q: 'Do you offer recurring services?',
        a: 'Yes — we offer weekly, bi-weekly, and monthly recurring options with discounted rates. Select your preferred frequency during booking.',
      },
    ],
  },
  {
    title: 'Scheduling & Policies',
    items: [
      {
        q: 'How far in advance should I book?',
        a: 'We recommend booking a few days in advance, especially for weekends and peak periods. Online bookings require at least 1 day notice so we can assign the right cleaning professional to your home.',
      },
      {
        q: 'What is your cancellation policy?',
        a: 'We require at least 24 hours notice for cancellations or changes to your appointment.',
      },
      {
        q: 'What areas do you serve?',
        a: "We proudly serve Los Angeles and surrounding areas. If you're outside our standard service zone, contact us — we're happy to accommodate when possible.",
      },
    ],
  },
  {
    title: 'What to Expect',
    items: [
      {
        q: 'Do I need to be home during the cleaning?',
        a: 'Not at all. Many clients provide access instructions during booking (gate codes, lockbox, concierge). Our team operates with professionalism, discretion, and care.',
      },
      {
        q: 'Do you bring your own supplies?',
        a: 'Yes — our team brings professional-grade products and tools. If you prefer specific products, just let us know in your booking notes.',
      },
      {
        q: 'How many cleaners will come?',
        a: 'Teams are assigned based on property size and scope. Smaller spaces may have one cleaner, while larger or more detailed homes may have a full team.',
      },
      {
        q: "What if I'm not satisfied with the service?",
        a: "Your satisfaction is our standard. If anything doesn't meet your expectations, notify us within 24 hours and we'll make it right.",
      },
      {
        q: 'What makes Lava & Co different?',
        a: 'We specialize in high-end properties and focus on consistency, detail, and presentation at a higher standard. Every space is treated with care, precision, and intention.',
      },
    ],
  },
];

const FAQItem = ({ q, a, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} data-testid={`faq-item-${id}`}>
      <button className="faq-question" onClick={() => setOpen(!open)} data-testid={`faq-question-${id}`}>
        <span>{q}</span>
        <ChevronDown size={18} className="faq-chevron" />
      </button>
      {open && <div className="faq-answer" data-testid={`faq-answer-${id}`}>{a}</div>}
    </div>
  );
};

const FAQPage = () => {
  return (
    <div className="faq-page">
      <section className="faq-hero">
        <div className="container">
          <h1 className="page-title font-heading">Frequently Asked Questions</h1>
          <p className="page-subtitle">Everything you need to know about our services</p>
        </div>
      </section>

      <section className="faq-content">
        <div className="container">
          {FAQ_SECTIONS.map((section, si) => (
            <div key={si} className="faq-section">
              <h2 className="faq-section-title">{section.title}</h2>
              <div className="faq-list">
                {section.items.map((item, ii) => (
                  <FAQItem key={ii} q={item.q} a={item.a} id={`${si}-${ii}`} />
                ))}
              </div>
            </div>
          ))}

          <div className="faq-cta">
            <h2 className="faq-cta-title">Still have questions?</h2>
            <p>Reach out to our team — we're happy to help.</p>
            <div className="faq-cta-actions">
              <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
              <Link to="/book" className="btn btn-primary" data-testid="faq-book-btn">Book a Cleaning</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
